/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { FormattedMessage } from 'react-intl';

import { Button, Typography2, breakpoints, typography2 } from '@coursera/cds-core';

import _t from 'i18n!nls/skills-common';

type Size = 'small' | 'medium' | 'large';

type Props = {
  className?: string;
  total: number;
  score: number;
  title: string;
  size?: Size;
  onTitleClick?: () => void;
  onScoreClick?: () => void;
  hideHeader?: boolean;
  showFullStar?: boolean;
};

const getTypographyVariant = (size: Size, context: 'title' | 'scoreText') => {
  switch (context) {
    case 'title':
      switch (size) {
        case 'large':
          return 'subtitleLarge';
        case 'medium':
          return 'actionPrimary';
        case 'small':
        default:
          return 'bodySecondary';
      }
    case 'scoreText':
      switch (size) {
        case 'large':
          return 'subtitleLarge';
        case 'medium':
          return 'bodyPrimary';
        case 'small':
        default:
          return 'bodySecondary';
      }
    default:
      return 'bodySecondary';
  }
};

const styles = {
  container: css`
    width: 100%;
  `,
  headerContainer: (size: Size) => css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: ${size === 'large' ? 'var(--cds-spacing-100)' : 'var(--cds-spacing-50)'};
    min-width: 0;
    gap: var(--cds-spacing-150);

    ${breakpoints?.down('xs')} {
      flex-direction: ${size === 'large' ? 'column' : 'row'};
      align-items: ${size === 'large' ? 'flex-start' : 'center'};
      gap: ${size === 'large' ? 'var(--cds-spacing-150)' : '0'};
    }
  `,
  button: (size: Size) => css`
    ${typography2[size === 'large' ? 'subtitleLarge' : 'bodySecondary']}
    max-width: 100%;
    padding: 0;
    background: none;
    hyphens: auto;
    text-align: start;
    white-space: normal;
    word-wrap: break-word;
    word-break: break-all;
    text-decoration: underline;

    &:hover,
    &:active {
      padding: 0;
      background: none;
    }

    ${breakpoints?.down('xs')} {
      max-width: 150px;
    }
  `,
  title: (size: Size) => css`
    max-width: 500px;
    white-space: normal;
    word-wrap: normal;
    word-break: normal;

    ${breakpoints?.down('sm')} {
      max-width: ${size === 'large' ? '400px' : '75%'};
    }

    ${breakpoints?.down('xs')} {
      max-width: ${size === 'large' ? '100%' : '75%'};
    }
  `,
  scoreContainer: (size: Size) => css`
    display: flex;
    align-items: center;
    gap: ${size === 'large' ? 'var(--cds-spacing-150)' : 'var(--cds-spacing-100)'};

    ${breakpoints?.down('xs')} {
      justify-content: ${size === 'large' ? 'space-between' : 'initial'};
      width: ${size === 'large' ? '100%' : 'initial'};
    }
  `,
  scoreText: (size: Size) => css`
    white-space: nowrap;

    ${breakpoints?.down('xs')} {
      ${typography2.subtitleMedium}
      color: var(--cds-color-neutral-primary);
    }
  `,
  starIcon: (widthPercent: number, size: Size) => css`
    width: ${size === 'large' ? '18px' : '16px'};
    height: ${size === 'large' ? '18px' : '16px'};
    border-radius: 50%;
    position: relative;
    background: ${widthPercent === 100 ? 'var(--cds-color-purple-700)' : 'var(--cds-color-neutral-disabled-strong)'};

    svg {
      width: ${size === 'large' ? '10px' : '8px'};
      height: ${size === 'large' ? '10px' : '8px'};
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  `,
  progressContainer: (size: Size) => css`
    position: relative;
    width: 100%;
    height: ${size === 'large' ? '8px' : '4px'};
    background-color: var(--cds-color-neutral-disabled-strong);
    border-radius: ${size === 'large' ? '16px' : '8px'};
    display: flex;
    align-items: center;
    align-self: stretch;
  `,
  progressBar: (widthPercent: number, size: Size) => () => {
    const minWidth = size === 'large' ? '8px' : '4px';
    const widthValue = widthPercent > 0 ? `${widthPercent}%` : minWidth;
    const border = `${size === 'large' ? '2px' : '1px'} solid var(--cds-color-white-0)`;

    return css`
      width: ${widthValue};
      height: ${size === 'large' ? '8px' : '4px'};
      background-color: var(--cds-color-purple-700);
      border-radius: ${size === 'large' ? '16px' : '4px'};
      transition: width 0.3s ease;
      border: ${widthPercent < 100 ? border : 'none'};
      box-sizing: content-box;

      /* Ensure minimum width */
      min-width: ${minWidth};
    `;
  },
};

const SkillProgressBar = ({
  total,
  score,
  title,
  onTitleClick,
  onScoreClick,
  size = 'large',
  className,
  hideHeader = false,
  showFullStar = false,
}: Props) => {
  if (total === 0) {
    return null;
  }

  const safeScore = Math.min(Math.max(score, 0), total);
  const progressPercent = Math.ceil((safeScore / total) * 100) || 0;
  const a11yScoreText = _t(
    '{score} out of {total} {count, plural, =1 {skill} other {skills}} mastered ({progressPercent}%)',
    {
      score: `${score}`,
      total: `${total}`,
      count: total,
      progressPercent,
    }
  );
  const scoreText =
    size === 'large' ? (
      <FormattedMessage
        message={_t('{score}/{total} {count, plural, =1 {skill} other {skills}} mastered')}
        score={`${score}`}
        total={`${total}`}
        count={total}
      />
    ) : (
      `${score}/${total}`
    );

  return (
    <div css={styles.container} className={className}>
      {!hideHeader && (
        <div css={styles.headerContainer(size)}>
          {onTitleClick ? (
            <Button
              variant="ghostSecondary"
              css={[styles.button(size), styles.title(size)]}
              onClick={onTitleClick}
              aria-label={_t('View skill progress dialog for {title}', { title })}
            >
              {title}
            </Button>
          ) : (
            <Typography2 component="h3" variant={getTypographyVariant(size, 'title')} css={styles.title(size)}>
              {title}
            </Typography2>
          )}

          <div css={styles.scoreContainer(size)}>
            {onScoreClick ? (
              <Button
                variant="ghostSecondary"
                css={[styles.button(size), styles.scoreText(size)]}
                onClick={onScoreClick}
                aria-label={_t(
                  '{score} out of {total} {count, plural, =1 {skill} other {skills}} mastered ({progressPercent}%). View skill progress dialog',
                  {
                    score: `${score}`,
                    total: `${total}`,
                    count: total,
                    progressPercent,
                  }
                )}
              >
                {scoreText}
              </Button>
            ) : (
              <Typography2
                component="span"
                variant={getTypographyVariant(size, 'scoreText')}
                css={styles.scoreText(size)}
              >
                {scoreText}
              </Typography2>
            )}
            <div css={styles.starIcon(showFullStar ? 100 : progressPercent, size)}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 8 8"
                fill="none"
                role="presentation"
                shapeRendering="geometricPrecision"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M3.82983 0.0256828C3.89484 0.00428215 3.96349 -0.00387312 4.03173 0.00170176C4.09996 0.00727668 4.16637 0.0264681 4.22701 0.0581349C4.28765 0.0898011 4.34129 0.133297 4.38474 0.18604C4.42712 0.23748 4.45895 0.296689 4.47847 0.360309L5.22696 2.66052H7.453C7.53717 2.65519 7.62147 2.67047 7.69851 2.70516C7.78125 2.74241 7.85269 2.80072 7.90564 2.87424L7.90623 2.87506C7.9452 2.92953 7.97288 2.99121 7.98764 3.05649C8.00241 3.12176 8.00396 3.18932 7.99221 3.25519C7.98046 3.32107 7.95565 3.38395 7.91922 3.44015C7.88465 3.4935 7.84027 3.53981 7.78845 3.57666L5.89118 5.02466L6.63005 7.34684C6.65946 7.42937 6.66621 7.5183 6.64949 7.60441C6.6321 7.694 6.59 7.77698 6.52791 7.84403L6.52225 7.85015L6.51626 7.85595C6.42092 7.94832 6.29321 8 6.16026 8C6.03438 8 5.9132 7.95368 5.81974 7.87035L4.00046 6.46589L2.16935 7.8838L2.16184 7.88893C2.09814 7.93249 2.02565 7.96162 1.94946 7.97428C1.87355 7.98689 1.7958 7.98285 1.72163 7.96244C1.65647 7.94463 1.59549 7.9141 1.54224 7.87263C1.48888 7.83108 1.44435 7.77936 1.41122 7.72049C1.37809 7.66162 1.35704 7.59677 1.34928 7.52972C1.34152 7.46267 1.34721 7.39474 1.36602 7.3299L1.36832 7.32196L2.10266 5.02479L0.185343 3.56582L0.176423 3.55787C0.120611 3.50811 0.0760264 3.4471 0.0456155 3.37889C0.0152043 3.31068 -0.000341321 3.23681 5.68381e-06 3.16216L4.31904e-05 3.1541L0.000505943 3.14604C0.00776191 3.01976 0.0613515 2.90055 0.15107 2.81112C0.240788 2.72168 0.360369 2.66825 0.487042 2.66102L0.495833 2.66052H2.7433L3.50784 0.334524L3.51163 0.325132C3.53954 0.255989 3.582 0.193613 3.63614 0.14221C3.69028 0.0908068 3.75485 0.0515716 3.82549 0.027148L3.82983 0.0256828ZM1.72163 7.96244L1.72079 7.96221L1.80315 7.66665L1.72163 7.96244Z"
                  fill="white"
                />
              </svg>
            </div>
          </div>
        </div>
      )}

      <div
        css={styles.progressContainer(size)}
        role="progressbar"
        aria-valuenow={progressPercent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuetext={a11yScoreText}
        aria-label={a11yScoreText}
      >
        <div css={styles.progressBar(progressPercent, size)} />
      </div>
    </div>
  );
};

export default SkillProgressBar;
